import { Form, Formik, FormikErrors, setIn } from 'formik';

import { ProductContext } from '../../../../../types/ProductContext';
import { Button } from '../../../../cdl/Button/Button';
import { Modal } from '../../../../cdl/Modal/Modal';
import { Text } from '../../../../cdl/Text/Text';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../../../common/buttons/ButtonGroup';
import { FormikEnumOptionSelect } from '../../../../common/form-elements/formik/FormikEnumOptionSelect';
import { FormikLubesProductTable } from '../../../../common/FormikProductTable/lubes/FormikLubesProductTable';
import { translate } from '../../../../common/helpers/translate.helper';
import { Item } from '../../../../common/models/item.model';
import { ItemModel } from '../../../../common/models/ItemModel';
import { Box } from '../../../../common/ui/Box';
import { OfferModel } from '../../../model/OfferModel';
import { OfferState } from '../../../model/OfferState';
import { useOfferUpdate } from '../hooks/useOfferUpdate';

interface ConfirmedStatusChangesRequiredModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    onSubmit: () => void;
    offer: OfferModel;
}

interface ConfirmedStatusChangesRequiredModalFormState {
    supplyMode: string;
    items: ItemModel[];
}

interface ModalFormikErrors extends FormikErrors<ConfirmedStatusChangesRequiredModalFormState> {
    invalidProducts?: boolean;
}

const validate = (values: ConfirmedStatusChangesRequiredModalFormState) => {
    let errors: ModalFormikErrors = {};

    if (!values.supplyMode) {
        errors.supplyMode = translate('offer.edit.lubes.supplyModeRequired');
    }

    values.items.forEach((item, index) => {
        if (
            !item.deleted &&
            (item.price?.value === undefined || item.price?.value?.toString() === '' || Number(item.price?.value) < 0)
        ) {
            errors = setIn(errors, `items.${index}.price.value`, true);
        }
    });

    if (values.items.every((item) => item.deleted)) {
        errors.invalidProducts = true;
    }

    return errors;
};

export const ConfirmedStatusChangesRequiredModal = (props: ConfirmedStatusChangesRequiredModalProps) => {
    const offerUpdateMutation = useOfferUpdate();
    const { addToast, addErrorToast } = useToasts();
    const initialFormState: ConfirmedStatusChangesRequiredModalFormState = {
        supplyMode: props.offer.supplyMode,
        items: props.offer.items.map((item: ItemModel) => {
            if (item.deleted) {
                return item;
            }
            return new Item({
                ...item,
                price: { value: item.price?.value || '', currency: item.price?.currency || 'USD' },
            });
        }) as unknown as ItemModel[],
    };

    const onSubmit = async (values: ConfirmedStatusChangesRequiredModalFormState) => {
        const updateOffer = {
            ...props.offer,
            state: OfferState.CONFIRMED,
            supplyMode: values.supplyMode,
            items: values.items,
        };
        try {
            await offerUpdateMutation.mutateAsync({ offer: updateOffer, offerId: updateOffer.id });
            addToast(translate('alert.state.CONFIRMED.title'));
        } catch {
            addErrorToast(translate('global.genericToastError'));
        }
        props.onDismiss();
    };

    return (
        <Modal isOpen={props.isOpen} onDismiss={props.onDismiss} scrollable={false} width={1350}>
            <Box padding={6} display="grid" rowGap={5}>
                <Text variant="title">{translate('alert.confirmorder.title')}</Text>

                <Text color="dark-blue.2">{translate('alert.confirmorder.datamissing')}</Text>

                <Formik initialValues={initialFormState} validate={validate} onSubmit={onSubmit}>
                    <Form>
                        <Box display="grid" rowGap={6}>
                            <Box rowGap={2} width={'50%'}>
                                <FormikEnumOptionSelect
                                    name="supplyMode"
                                    enumType="supplyMode"
                                    label={translate('offer.edit.lubes.supplyMode')}
                                    context={ProductContext.LUBES}
                                />
                            </Box>
                            <FormikLubesProductTable
                                config={{
                                    initialItems: initialFormState.items,
                                    showPrices: true,
                                    editable: false,
                                    editSurcharges: false,
                                    editPrices: true,
                                }}
                            />
                            <ButtonGroup justifyContent="end">
                                <Button emphasis="high" isLoading={offerUpdateMutation.isPending} type="submit">
                                    {translate('alert.confirmorder.btnok')}
                                </Button>
                            </ButtonGroup>
                        </Box>
                    </Form>
                </Formik>
            </Box>
        </Modal>
    );
};
